function initSlider(root) {
  const type = root.getAttribute('data-type') || 'collection';
  const autoplay = root.getAttribute('data-autoplay') === 'true';
  const delay = parseInt(root.getAttribute('data-delay') || '4000', 10);
  const dotActiveClass = root.getAttribute('data-dot-active') || 'bg-opacity-100';
  const dotInactiveClass = root.getAttribute('data-dot-inactive') || 'bg-opacity-30';

  const viewport = root.querySelector('[data-slider-viewport]') || root;
  const track = root.querySelector('[data-slider-track]');
  if (!track) return;

  const slides = Array.from(track.children);
  const prevBtn = root.querySelector('[data-slider-prev]');
  const nextBtn = root.querySelector('[data-slider-next]');
  const dotsContainer = root.querySelector('[data-slider-dots]');
  const gap = parseInt(root.getAttribute('data-gap') || '16', 10);

  let bpConfig;
  try {
    bpConfig = JSON.parse(root.getAttribute('data-breakpoints') || '{}');
  } catch {
    bpConfig = {};
  }

  let currentIndex = 0;
  let cardsPerPage = 1;
  let totalPages = 1;
  let autoplayTimer = null;
  let isDragging = false;
  let startX = 0;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function getCardsPerPage() {
    if (type !== 'collection') return 1;
    const w = window.innerWidth;
    const entries = Object.entries(bpConfig)
      .map(([k, v]) => [parseInt(k, 10), parseInt(v, 10)])
      .filter(([k, v]) => !Number.isNaN(k) && !Number.isNaN(v))
      .sort((a, b) => a[0] - b[0]);

    let cpp = 1;
    for (const [bp, val] of entries) {
      if (w >= bp) cpp = val;
    }
    return Math.max(1, cpp);
  }

  function setTransform(pxOrPercent) {
    track.style.transform = `translateX(${pxOrPercent})`;
  }

  function updateDots() {
    if (!dotsContainer) return;
    const dots = dotsContainer.querySelectorAll('[data-slider-dot]');
    dots.forEach((dot, i) => {
      const active = i === currentIndex;
      dot.classList.toggle(dotActiveClass, active);
      dot.classList.toggle(dotInactiveClass, !active);
      dot.setAttribute('aria-selected', String(active));
    });
  }

  function genDots() {
    if (!dotsContainer) return;
    dotsContainer.innerHTML = '';

    const count = type === 'benefits' ? slides.length : totalPages;
    if (count <= 1) {
      dotsContainer.style.display = 'none';
      return;
    }
    dotsContainer.style.display = 'flex';

    for (let i = 0; i < count; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('data-slider-dot', '');
      btn.setAttribute('aria-label', `Go to ${type === 'benefits' ? 'slide' : 'page'} ${i + 1}`);
      btn.className = `w-12 h-[2px] transition-colors duration-200 ${dotInactiveClass}`;
      btn.addEventListener('click', () => {
        goTo(i);
        restartAutoplay();
      });
      dotsContainer.appendChild(btn);
    }
  }

  function goTo(index) {
    if (type === 'benefits') {
      const max = slides.length - 1;
      currentIndex = clamp(index, 0, max);
      setTransform(`-${currentIndex * 100}%`);
      updateDots();
      return;
    }

    currentIndex = clamp(index, 0, totalPages - 1);

    const firstCard = slides[0];
    if (!firstCard) return;
    const cardWidth = firstCard.offsetWidth;
    const pageOffset = -(currentIndex * cardsPerPage * (cardWidth + gap));
    setTransform(`${pageOffset}px`);

    updateDots();
    if (prevBtn) prevBtn.disabled = currentIndex === 0;
    if (nextBtn) nextBtn.disabled = currentIndex === totalPages - 1;
  }

  function next() {
    goTo(currentIndex + 1);
  }
  function prev() {
    goTo(currentIndex - 1);
  }

  function startAutoplay() {
    if (!autoplay) return;
    stopAutoplay();
    autoplayTimer = setInterval(() => {
      if (type === 'benefits') {
        const max = slides.length - 1;
        goTo(currentIndex >= max ? 0 : currentIndex + 1);
      } else {
        goTo(currentIndex >= totalPages - 1 ? 0 : currentIndex + 1);
      }
    }, delay);
  }
  function stopAutoplay() {
    if (autoplayTimer) {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  }
  function restartAutoplay() {
    stopAutoplay();
    startAutoplay();
  }

  function initBenefits() {
    genDots();
    goTo(0);
  }

  function initCollection() {
    cardsPerPage = getCardsPerPage();
    totalPages = Math.max(1, Math.ceil(slides.length / cardsPerPage));

    genDots();
    goTo(currentIndex);

    if (prevBtn)
      prevBtn.onclick = () => {
        prev();
        restartAutoplay();
      };
    if (nextBtn)
      nextBtn.onclick = () => {
        next();
        restartAutoplay();
      };
  }

  (viewport || root).addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
      restartAutoplay();
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
      restartAutoplay();
    }
  });

  root.addEventListener('mouseenter', stopAutoplay);
  root.addEventListener('mouseleave', startAutoplay);

  root.addEventListener(
    'touchstart',
    (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
      stopAutoplay();
    },
    { passive: true },
  );

  root.addEventListener('touchmove', () => {}, { passive: true });

  root.addEventListener('touchend', (e) => {
    if (!isDragging) return;
    isDragging = false;
    const diff = startX - e.changedTouches[0].clientX;
    const threshold = 50;
    if (Math.abs(diff) > threshold) diff > 0 ? next() : prev();
    startAutoplay();
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (type === 'collection') {
        const newCpp = getCardsPerPage();
        if (newCpp !== cardsPerPage) {
          cardsPerPage = newCpp;
          totalPages = Math.max(1, Math.ceil(slides.length / cardsPerPage));
          genDots();
        }
        if (currentIndex >= totalPages) currentIndex = totalPages - 1;
      }
      goTo(currentIndex);
    }, 200);
  });

  if (type === 'benefits') initBenefits();
  else initCollection();
  startAutoplay();
}

window.initRecommendedSlider = initSlider;

document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('[data-slider]');
  if (!sliders.length) return;

  sliders.forEach(initSlider);
});
